import { cn } from "@/utils";
import { Upload } from "lucide-react";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

interface CanvasOsFileDropZoneProps {
  /**
   * Called when one or more OS files are dropped anywhere inside the
   * zone. `null` disables the zone (e.g. when no scope is loaded).
   */
  onFiles: ((files: File[]) => void) | null;
  children: React.ReactNode;
  className?: string;
  /**
   * Optional onboarding-spotlight target id. When set, the root
   * element carries `data-onboarding-target="<id>"` so the
   * `OnboardingSpotlight` halo can find this region by stable id
   * without coupling the explorer to the onboarding implementation.
   */
  "data-onboarding-target"?: string;
}

/**
 * Native HTML5 drop zone for OS files / folders dropped from the
 * desktop into the canvas explorer pane.
 *
 * Strictly filters on `dataTransfer.types.includes("Files")` so it
 * never fires for in-app dnd-kit drags (which set no `dataTransfer`
 * at all — pointer drags don't enter the HTML5 DnD API). The two
 * systems coexist cleanly: dnd-kit owns in-app drags, this owns
 * desktop-origin drags.
 *
 * Mirrors `WorkspaceDropZone`'s drag-counter semantics so nested
 * children (rows, scope chips) don't flicker the highlight as the
 * cursor moves around.
 */
export const CanvasRailOsDropZone = forwardRef<
  HTMLDivElement,
  CanvasOsFileDropZoneProps
>(function CanvasRailOsDropZone(
  {
    onFiles,
    children,
    className,
    "data-onboarding-target": dataOnboardingTarget,
  },
  ref,
) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  // Reset on unmount so a fast unmount mid-drag doesn't leave the
  // counter stuck above zero (the next mount would start "stuck open").
  useEffect(
    () => () => {
      dragCounter.current = 0;
    },
    [],
  );

  const isFileDrag = (e: React.DragEvent): boolean =>
    e.dataTransfer.types.includes("Files");

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;
      if (!onFiles) return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) onFiles(files);
    },
    [onFiles],
  );

  return (
    <div
      ref={ref}
      className={cn("relative h-full", className)}
      data-onboarding-target={dataOnboardingTarget}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
      {isDragging && onFiles && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-foreground/30 bg-card/90 px-8 py-7 shadow-2xl">
            <Upload className="h-8 w-8 text-foreground/40" />
            <p className="text-xs font-medium text-foreground/70">
              Drop files to upload
            </p>
          </div>
        </div>
      )}
    </div>
  );
});
